import {
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppSharedModule } from 'src/app/app-shared/app-shared.module';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'w-h-dropzone',
  styles: `
    :host {
      width: 100%;
    }
  `,
  imports: [AppSharedModule, TranslateModule, DragDropModule],
  template: `
    <div
      cdkDropList
      [id]="dropzoneId"
      (cdkDropListDropped)="onDrop($event)"
      style="height: 100%;"
      class="flex--r-s-sb gap--4"
      #wrapper
    >
      <ng-content></ng-content>
    </div>
  `,
})
export class WhDropzoneComponent {
  @ViewChild('wrapper') wrapper: ElementRef;
  @ContentChildren('matTreeNode', { read: ElementRef })
  nodes: QueryList<ElementRef>;

  @Input({ required: true }) dropzoneId: string;
  @Input() showPlaceholder: boolean;
  @Input() pendingAssignment: boolean;
  @Input() dropzoneType: string;
  @Output() handleDrop = new EventEmitter();

  private _isProcessingDrop = false;

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event): void {
    // Guard: skip if a CDK drop already triggered onDrop for this gesture
    if (this._isProcessingDrop) return;

    if (
      event?.target instanceof HTMLElement &&
      event?.target?.className?.includes('dnd-placeholder-box')
    ) {
      this.onDrop(event);
    }
  }

  onDrop(event: any) {
    this._isProcessingDrop = true;
    // Reset flag after current microtask so subsequent gestures work
    Promise.resolve().then(() => (this._isProcessingDrop = false));
    let id = null;
    if (this.dropzoneType === 'table') {
      const dropPosition = event.dropPoint;
      const droppedRow = this.getRowAtPosition(dropPosition);

      if (droppedRow) {
        id = droppedRow.getAttribute('id');
      }
    } else if (this.dropzoneType === 'tree') {
      const dropPosition = event.event
        ? { x: event.event.clientX, y: event.event.clientY }
        : event.clientX && event.clientY
          ? { x: event.clientX, y: event.clientY }
          : { x: 0, y: 0 };
      const droppedNode = this.getNodeAtPosition(dropPosition);

      if (droppedNode) {
        // Try to get id from data-node-id attribute first, then fall back to id attribute
        id =
          (droppedNode as any).getAttribute('data-node-id') ||
          (droppedNode as any).id;
      }
    }

    this.handleDrop.emit({
      event,
      id: id || event?.event?.toElement?.id || event?.target?.id || null,
    });
  }

  private getRowAtPosition(dropPosition: {
    x: number;
    y: number;
  }): HTMLElement | null {
    const rows = this.wrapper.nativeElement.querySelectorAll('mat-row');

    for (let i = 0; i < rows.length; i++) {
      const rowRect = rows[i].getBoundingClientRect();
      if (dropPosition?.y >= rowRect.top && dropPosition?.y <= rowRect.bottom) {
        return rows[i];
      }
    }
    return null;
  }

  private getNodeAtPosition(dropPosition: {
    x: number;
    y: number;
  }): ElementRef | null {
    const nodes = this.wrapper.nativeElement.querySelectorAll('.mat-tree-node');

    for (let i = 0; i < nodes.length; i++) {
      const nodeRect = nodes[i].getBoundingClientRect();
      if (
        dropPosition?.y >= nodeRect.top &&
        dropPosition?.y <= nodeRect.bottom
      ) {
        return nodes[i];
      }
    }
    return null;
  }
}
